/**
 * t657u/t658u: "X is typing…" in chat, shared by the web (computer and phone) and the app. Typers are kept per
 * conversation, or per thread (conversation + root message) for thread replies, and drop out after TYPING_TTL_MS
 * without another typing ping, or straight away when their message arrives.
 */
export const TYPING_TTL_MS = 6000;
/** A composer sends a typing ping at most this often while text is being typed. */
export const TYPING_SEND_EVERY_MS = 3000;

/** The conversation's main stream, or one thread in it. */
export const typingKey = (conversationId: string, parentId?: string | null) => (parentId ? `${conversationId}:${parentId}` : conversationId);

/** key → user id → expiry (ms since epoch). */
export type TypingMap = Record<string, Record<string, number>>;

export function addTyper(state: TypingMap, key: string, userId: string, now = Date.now(), ttl = TYPING_TTL_MS): TypingMap {
  return { ...state, [key]: { ...(state[key] ?? {}), [userId]: now + ttl } };
}

/** Their message arrived (or they left): they're no longer typing there. Same object back when nothing changed. */
export function removeTyper(state: TypingMap, key: string, userId: string): TypingMap {
  const cur = state[key];
  if (!cur || !(userId in cur)) return state;
  const rest = { ...cur };
  delete rest[userId];
  const next = { ...state };
  if (Object.keys(rest).length) next[key] = rest; else delete next[key];
  return next;
}

/** Drops expired typers. Same object back when nothing expired (so stores don't re-render). */
export function pruneTypers(state: TypingMap, now = Date.now()): TypingMap {
  let changed = false;
  const next: TypingMap = {};
  for (const [key, users] of Object.entries(state)) {
    const live = Object.fromEntries(Object.entries(users).filter(([, exp]) => exp > now));
    if (Object.keys(live).length !== Object.keys(users).length) changed = true;
    if (Object.keys(live).length) next[key] = live;
  }
  return changed ? next : state;
}

/** Who is typing under this key right now, in the order they started, never yourself. */
export function typersFor(state: TypingMap, key: string, me?: string | null, now = Date.now()): string[] {
  return Object.entries(state[key] ?? {}).filter(([id, exp]) => exp > now && id !== me).map(([id]) => id);
}

/** "Sam is typing…", "Sam and Alex are typing…", "Sam, Alex and 2 others are typing…"; null when nobody is. */
export function typingLine(names: string[]): string | null {
  const n = names.map(x => x || 'Someone');
  if (!n.length) return null;
  if (n.length === 1) return `${n[0]} is typing…`;
  if (n.length === 2) return `${n[0]} and ${n[1]} are typing…`;
  if (n.length === 3) return `${n[0]}, ${n[1]} and ${n[2]} are typing…`;
  return `${n[0]}, ${n[1]} and ${n.length - 2} others are typing…`;
}
